import { Component, input, signal, computed, inject, DestroyRef, effect, untracked, HostBinding } from '@angular/core';
import { ActivityOwner } from '../../entities/activity/activity-owner';
import { Activity } from '../../entities/activity/activity';
import * as fromGraphQl from './graphql';
import { ActivityFilter } from '../../entities/activity/activity-filter';
import { SortDirection } from '../../entities/sort/sort';
import { ReactiveContainer } from '../../containers/reactive-container';
import { FilterStateService } from '../../services/filter-state/filter-state.service';
import { ActivityStreamFilterState, DEFAULT_ACTIVITY_STREAM_FILTER_STATE, isValidActivityStreamFilterState } from './activity-stream-filter-state.interface';
import { RefetchCoordinator } from '../../services/refetch-coordinator/refetch-coordinator.service';

interface SortOption {
  label: string;
  fieldName: string;
  direction: SortDirection;
  key: string;
}

interface FilterOption {
  label: string;
  key: string;
}

@Component({
  selector: 'app-activity-stream',
  templateUrl: './activity-stream.component.html',
  styleUrls: ['./activity-stream.component.scss']
})
export class ActivityStreamComponent {
  private readonly filterStateService = inject(FilterStateService);
  private readonly destroyRef = inject(DestroyRef);
  private refetchCoordinator = inject(RefetchCoordinator);

  readonly owner = input.required<ActivityOwner>();
  readonly filterStorageKey = input<string>();
  readonly commentsOnly = input<boolean>(false);
  readonly title = input<string>('Aktivitäten');

  readonly filterState = signal<ActivityStreamFilterState>(DEFAULT_ACTIVITY_STREAM_FILTER_STATE);

  readonly sortOptions: SortOption[] = [
    { label: 'Älteste zuerst', fieldName: 'createdDateTime', direction: 'asc', key: 'createdDateTimeAsc' },
    { label: 'Neuste zuerst', fieldName: 'createdDateTime', direction: 'desc', key: 'createdDateTimeDesc' }
  ];

  readonly filterOptions: FilterOption[] = [
    { label: 'Alle Aktivitäten', key: 'allActivities' },
    { label: 'Nur Kommentare', key: 'commentsOnly' }
  ];

  readonly sortModel = computed(() => {
    const sortKey = this.filterState().sortKey;
    return this.sortOptions.find(opt => opt.key === sortKey) ?? this.sortOptions[1];
  });

  readonly filterModel = computed(() => {
    const filterKey = this.filterState().filterKey;
    return this.filterOptions.find(opt => opt.key === filterKey) ?? this.filterOptions[0];
  });

  readonly currentPage = signal<number>(0);
  readonly pageSize = 200;

  private _stateRestored = false;

  constructor() {
    effect(() => {
      const key = this.filterStorageKey();
      if (key && !this._stateRestored) {
        this._stateRestored = true;
        const savedState = this.filterStateService.restore<ActivityStreamFilterState>(
          key,
          isValidActivityStreamFilterState
        );
        if (savedState) {
          untracked(() => this.filterState.set(savedState));
        }
      }
    });

    effect(() => {
      const key = this.filterStorageKey();
      const state = this.filterState();
      untracked(() => {
        if (key) {
          this.filterStateService.save(key, state);
        }
      });
    });
  }

  private readonly container = new ReactiveContainer<fromGraphQl.ActivitiesQueryResponse>(
    fromGraphQl.ACTIVITIES_QUERY,
    { useGlobalLoading: false }
  );

  readonly data = this.container.fromSignals(
    [this.owner, this.sortModel, this.filterModel, this.currentPage],
    ([owner, sort, filterOpt, page]) => {
      const filter = this.buildFilter(owner, sort, filterOpt, page);
      return { filter };
    },
    { skipWhen: ([owner]) => !owner }
  );

  readonly activities = this.container.selectWithDefault(d => d.activities.content, [] as Activity[]);
  readonly loading = this.container.loading;
  readonly error = this.container.error;

  readonly totalElements = computed(() => this.container.data()?.activities?.totalElements || 0);
  readonly totalPages = computed(() => this.container.data()?.activities?.totalPages || 0);
  readonly hasNextPage = computed(() => this.currentPage() < this.totalPages() - 1);
  readonly hasPreviousPage = computed(() => this.currentPage() > 0);
  readonly showPagination = computed(() => this.totalElements() > this.pageSize);

  reload(skipRefetch = false): void {
    this.container.reload();
    if (!skipRefetch) {
      this.refetchCoordinator.emit({ entityType: this.owner()?.entityType, entityId: this.owner()?.id });
    }
  }

  onSortChange(option: SortOption): void {
    this.filterState.update(s => ({ ...s, sortKey: option.key }));
  }

  onFilterChange(option: FilterOption): void {
    this.filterState.update(s => ({ ...s, filterKey: option.key }));
    this.currentPage.set(0);
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages() - 1) {
      this.currentPage.update(page => page + 1);
    }
  }

  previousPage(): void {
    if (this.currentPage() > 0) {
      this.currentPage.update(page => page - 1);
    }
  }

  private buildFilter(owner: ActivityOwner, sort: SortOption, filterOpt: FilterOption, page: number): ActivityFilter {
    const filter = new ActivityFilter(
      [sort.fieldName],
      sort.direction,
      page,
      this.pageSize
    );
    filter.ownerId = owner.id;
    filter.ownerType = owner.entityType;
    if (this.commentsOnly() || filterOpt.key === 'commentsOnly') {
      filter.activityTypes = ['NOTE_CREATED'];
    }
    return filter;
  }
}
